import React, { useEffect, useState } from 'react';

/**
 * Input level, in dBFS, below which the clone recording is called too quiet. Tuning knob: browsers apply their own
 * gain control, so a normal speaking voice close to the mic lands around -25 to -15.
 */
const CLONE_QUIET_DBFS = -30;

/** Bottom of the meter's scale in dBFS; anything quieter shows as an empty bar. */
const CLONE_METER_FLOOR_DBFS = -60;

/**
 * Live microphone level shown while the clone sample records, so the user can tell they are loud enough.
 *
 * @param {Object} props - The stream being recorded, the styles and t.
 * @returns {ReactElement}
 */
const CloneLevelMeter = ({ stream, styles, t }) => {
    const [ dbfs, setDbfs ] = useState(CLONE_METER_FLOOR_DBFS);

    useEffect(() => {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        const samples = new Float32Array(analyser.fftSize);
        let frame;

        context.createMediaStreamSource(stream).connect(analyser);

        const tick = () => {
            analyser.getFloatTimeDomainData(samples);
            const rms = Math.sqrt(samples.reduce((sum, v) => sum + (v * v), 0) / samples.length);
            const level = Math.max(CLONE_METER_FLOOR_DBFS, 20 * Math.log10(rms || 1e-6));

            // Fall slowly, rise instantly, so the bar and hint don't flicker between words.
            setDbfs(previous => Math.max(level, previous - 1));
            frame = requestAnimationFrame(tick);
        };

        tick();

        return () => {
            cancelAnimationFrame(frame);
            context.close();
        };
    }, [ stream ]);

    const quiet = dbfs < CLONE_QUIET_DBFS;
    const color = quiet ? '#d9822b' : '#2e9e5b';
    const percent = Math.round(100 * (dbfs - CLONE_METER_FLOOR_DBFS) / -CLONE_METER_FLOOR_DBFS);

    return (
        <div>
            <div className = { styles.meterTrack }>
                <div
                    className = { styles.meterFill }
                    style = {{ width: `${percent}%`,
                        backgroundColor: color }} />
            </div>
            <p
                aria-live = 'polite'
                className = { styles.meterHint }
                style = {{ color }}>
                { t(quiet ? 'transcribing.cloneVoiceMeterTooQuiet' : 'transcribing.cloneVoiceMeterGood') }
            </p>
        </div>
    );
};

export default CloneLevelMeter;
